import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from '../../../Context/SnackbarContext';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  Paper,
  Tooltip
} from '@mui/material';
import { ProgressBar } from '../../Atoms/ProgressBar';
import {
  DEFAULT_ADVANCE_BUTTON_STYLE,
  DEFAULT_CANCEL_BUTTON_STYLE,
  MODAL_CONTENT_STYLE
} from '../../../utils/styleHelpers';
import {
  AssetMigrationValidationResponse,
  AssetFieldMapping,
  AssetMigrationRecord,
  AssetMigrationResult,
  migrateAssets
} from '../../../store/OutsideInventory';
import { getAccountName } from '../../../store/Accounts';

const PROGRESS_ANIMATION_DURATION_MS = 15000;
const PROGRESS_TICK_INTERVAL_MS = 500;
const PROGRESS_TARGET_BEFORE_COMPLETE = 90;

const TRUNCATED_CELL_SX = {
  maxWidth: 160,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  height: 36
} as const;

const TruncatedCell = ({ value }: { value: string }) => (
  <Tooltip
    title={value}
    placement="top"
    enterDelay={300}>
    <TableCell sx={TRUNCATED_CELL_SX}>{value}</TableCell>
  </Tooltip>
);

interface MigrationReviewStepProps {
  validationResponse: AssetMigrationValidationResponse;
  fieldMappings: AssetFieldMapping[];
  validating: boolean;
  confirmModalOpen: boolean;
  onConfirmModalClose: () => void;
  onReset: () => void;
  onHideCtas: (hidden: boolean) => void;
  validationError?: string | null;
  onRetryValidation?: () => void;
}

export const MigrationReviewStep = ({
  validationResponse,
  fieldMappings,
  validating,
  confirmModalOpen,
  onConfirmModalClose,
  onReset,
  onHideCtas,
  validationError,
  onRetryValidation
}: MigrationReviewStepProps) => {
  const { setSnack } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [migrating, setMigrating] = useState(false);
  const [migrationDone, setMigrationDone] = useState(false);
  const [migrationError, setMigrationError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [accountNames, setAccountNames] = useState<Record<string, string>>({});
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const previewRows = validationResponse.preview.slice(0, 10);
  const { valid_count, error_count, errors, valid_asset_ids } = validationResponse;

  const contentFieldMappings = fieldMappings.filter(
    (m) => m.portalFieldKey !== 'account_id' && m.portalFieldKey !== 'supplier_id' && m.portalFieldKey !== 'product_id'
  );

  useEffect(() => {
    const uniqueAccountIds = [...new Set(previewRows.map((row) => row.account_id).filter(Boolean))];
    if (uniqueAccountIds.length === 0) return;

    Promise.all(uniqueAccountIds.map((id) => getAccountName(id).then((name) => ({ id, name })))).then((results) => {
      const nameMap: Record<string, string> = {};
      results.forEach(({ id, name }) => {
        nameMap[id] = name;
      });
      setAccountNames(nameMap);
    });
  }, [validationResponse]);

  const getPreviewCellValue = (row: AssetMigrationRecord, portalFieldKey: string): string => {
    const value = row.content[portalFieldKey as keyof typeof row.content];
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return '';
    return String(value);
  };

  const clearProgressInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const startProgressAnimation = () => {
    setProgress(0);
    const ticksTotal = PROGRESS_ANIMATION_DURATION_MS / PROGRESS_TICK_INTERVAL_MS;
    const increment = PROGRESS_TARGET_BEFORE_COMPLETE / ticksTotal;
    let current = 0;

    progressIntervalRef.current = setInterval(() => {
      current = Math.min(current + increment, PROGRESS_TARGET_BEFORE_COMPLETE);
      setProgress(current);
      if (current >= PROGRESS_TARGET_BEFORE_COMPLETE) clearProgressInterval();
    }, PROGRESS_TICK_INTERVAL_MS);
  };

  useEffect(() => {
    return () => clearProgressInterval();
  }, []);

  const handleInsertConfirm = async () => {
    onConfirmModalClose();
    setMigrating(true);
    onHideCtas(true);
    startProgressAnimation();

    const result: { data: AssetMigrationResult | null; error: { message: string } | null } = await migrateAssets(
      valid_asset_ids,
      fieldMappings
    );

    clearProgressInterval();
    setProgress(100);

    if (result.error) {
      setMigrationError(result.error.message);
      setSnack({ message: result.error.message, type: 'error', open: true });
      onHideCtas(false);
    }

    setMigrating(false);
    setMigrationDone(true);
  };

  if (validating) {
    return <Box data-testid="validation-loading" />;
  }

  if (validationError) {
    return (
      <Box
        sx={{ mt: 2 }}
        data-testid="validation-error">
        <Typography
          variant="body2"
          color="error"
          sx={{ mb: 1 }}>
          {validationError}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {onRetryValidation && (
            <button
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              onClick={onRetryValidation}
              data-testid="validation-retry-btn">
              Retry
            </button>
          )}
          <button
            className={DEFAULT_CANCEL_BUTTON_STYLE}
            onClick={onReset}
            data-testid="validation-start-over-btn">
            Start Over
          </button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, maxWidth: '100%', overflow: 'hidden' }}>
      {(migrating || (migrationDone && !migrationError)) && (
        <Box
          sx={{ mb: 2 }}
          data-testid="migration-progress">
          {migrating && (
            <Typography
              variant="body2"
              sx={{ mb: 1 }}>
              Migration in progress…
            </Typography>
          )}
          <ProgressBar
            progress={progress}
            className="w-full rounded"
            progressBarStyleOverride="rounded h-full bg-main-100 transition"
          />
        </Box>
      )}

      {migrationError && (
        <Box
          sx={{ mb: 2 }}
          data-testid="migration-error">
          <Typography
            variant="body2"
            color="error"
            sx={{ mb: 1 }}>
            {migrationError}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <button
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              onClick={handleInsertConfirm}
              data-testid="migration-retry-btn">
              Retry
            </button>
            <button
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              onClick={onReset}
              data-testid="migration-start-over-btn">
              Start Over
            </button>
          </Box>
        </Box>
      )}

      {migrationDone && !migrationError && (
        <Box
          sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
          data-testid="migration-complete">
          <Typography
            variant="body2"
            sx={{ mb: 1, color: 'success.main' }}>
            Migration complete.
          </Typography>
        </Box>
      )}

      {!migrationDone && (
        <>
          <Tabs
            value={activeTab}
            onChange={(_e, val) => setActiveTab(val)}
            sx={{ mb: 2 }}>
            <Tab
              label={`Preview for Insert (${valid_count})`}
              data-testid="preview-tab"
            />
            <Tab
              label={`Errors (${error_count})`}
              data-testid="errors-tab"
            />
          </Tabs>

          {activeTab === 0 && (
            <Box data-testid="preview-panel">
              <Typography
                variant="body2"
                sx={{ mb: 1 }}>
                Showing up to 10 of {valid_count} valid asset{valid_count !== 1 ? 's' : ''} to be inserted.
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table
                  size="small"
                  sx={{ '& td, & th': { border: 1, borderColor: 'divider' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Product</TableCell>
                      {contentFieldMappings.map((m) => (
                        <TableCell key={m.portalFieldKey}>{m.portalField}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-testid={`preview-row-${row.id}`}>
                        <TruncatedCell value={row.id} />
                        <TruncatedCell value={accountNames[row.account_id] ?? row.account_id} />
                        <TruncatedCell value={row.content.supplier?.name ?? ''} />
                        <TruncatedCell
                          value={row.content.product?.nameAbbreviation || row.content.product?.name || ''}
                        />
                        {contentFieldMappings.map((m) => (
                          <TruncatedCell
                            key={m.portalFieldKey}
                            value={getPreviewCellValue(row, m.portalFieldKey)}
                          />
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box data-testid="errors-panel">
              <Typography
                variant="body2"
                sx={{ mb: 1 }}>
                {error_count} asset{error_count !== 1 ? 's' : ''} have validation errors and will not be inserted.
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table
                  size="small"
                  sx={{ '& td, & th': { border: 1, borderColor: 'divider' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>GRI (Asset ID)</TableCell>
                      <TableCell>Asset Number</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errors.map((err, idx) => (
                      <TableRow
                        key={`${err.asset_id}-${idx}`}
                        data-testid={`error-row-${err.asset_id}`}>
                        <TruncatedCell value={err.asset_id} />
                        <TruncatedCell value={err.asset_number} />
                        <TruncatedCell value={err.error} />
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </>
      )}

      <Modal
        open={confirmModalOpen}
        onClose={onConfirmModalClose}
        aria-labelledby="confirm-migration-title"
        data-testid="confirm-modal">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 420
          }}>
          <Paper sx={MODAL_CONTENT_STYLE}>
            <Typography
              id="confirm-migration-title"
              variant="h6"
              sx={{ mb: 2 }}>
              Confirm Migration
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 3 }}>
              Are you sure you want to insert {valid_count} asset{valid_count !== 1 ? 's' : ''} into outside inventory?
              This action cannot be undone.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <button
                className={DEFAULT_CANCEL_BUTTON_STYLE}
                onClick={onConfirmModalClose}
                data-testid="confirm-modal-cancel-btn">
                Cancel
              </button>
              <button
                className={DEFAULT_ADVANCE_BUTTON_STYLE}
                onClick={handleInsertConfirm}
                data-testid="confirm-modal-confirm-btn">
                Confirm
              </button>
            </Box>
          </Paper>
        </Box>
      </Modal>
    </Box>
  );
};
